import { get, chain, pick, size, map, isFinite, reduce, set, identity, find, compact, isEmpty, updateWith, isNumber, forOwn, isObject, mapValues, filter, includes, findIndex, some, slice, cloneDeep, omit, isNaN, concat, orderBy as _orderBy } from "lodash"
import { createSelector } from "reselect"
import { formatLabel } from "helpers/securities"
import { limitPrecision } from "helpers/math"
import { getIncludeNotHeld, getEquityOverride, getGroupedPortfolioPositions, getGroupedRebalancedPortfolioPositions, getRebalancedAssetHoldings, getRawPositionHoldings, getPositionsDelta } from "./positions"
import { getFactorsCategories } from "./categories"
import { getContributors, getRebalancedContributors, getGroupedContributors, getGroupedRebalancedContributors } from "./contributors"
import { getAllColumns, getColumnsByCategory } from "./columns"
import { ITEMS_PER_PAGE, getDecompositionType, getOrder, getRebalanceDate, getSelectedPage, getSimulateActions, getActionsBase } from "."
import { getIsActiveAndNotIntraday } from "./intraday"
import { computeSimulationExperiment } from "hooks/rebalancer"
import { buildContributorKey } from "helpers/contributors"
import { NOTHELD_CONTRIBUTOR, isMatchingContributorOrSubContributor } from "../helpers"
import { computeNumberOfTrades, filterAndOrderTrades, computeTradeBuckets } from "helpers/simulator"
import { isCashOffsetContributor } from "helpers/assets"
import { percentageFormatter } from "helpers/formatters/numbers"
import { getRebalancedSummary, getSummary } from "./summary"
import { getGrouping, getSelectedGroup } from "./grouping"
import { Creators } from "../model"
import { getIsActive } from "modules/portfolio-root/selectors/portfolios"

const EMPTY_OBJECT = {}
const getEditingSecurity = state => get(state, "modules.rebalance.editing")

export const getFactorsCategoriesHeader = createSelector(
  [getFactorsCategories, getDecompositionType],
  (factorCategories, decompositionType) => {
    if (isEmpty(factorCategories)) {
      return null
    }
    const mappedCategories = map(factorCategories, factorCategory => ({ id: factorCategory.id, primaryText: factorCategory.label }))
    if (decompositionType === "risk") {
      return [
        { id: "all", primaryText: "Factors" },
        ...mappedCategories,
      ]
    }
    return mappedCategories
  },
)

const getContributorsWithOriginalProperty = createSelector(
  [getContributors],
  (contributors) => mapValues(contributors, contributor => ({
    ...contributor,
    // We need to keep the initial values of the security inside the contributor for the UI to do calculations
    original: pick(contributor, ["economicExposure", "positionPercentEquity", "activePercentEquity", "isNotHeld"]),
  })),
)

export const getRawCombinedContributors = createSelector(
  [getContributorsWithOriginalProperty, getRebalancedContributors],
  combineContributors,
)

const getTradedSecuritiesMap = createSelector(
  [getPositionsDelta],
  (positionsDelta) => chain(positionsDelta)
    .keyBy(buildContributorKey)
    .omit("undefined")
    .mapValues(() => true)
    .value(),
)

const getAllTrades = createSelector(
  [getContributorsWithOriginalProperty, getTradedSecuritiesMap, getRawCombinedContributors],
  (originalPositions, tradedSecuritiesMap, combinedContributors) => chain(combinedContributors)
    .filter((contributor) => !contributor.isNotHeld && !isCashOffsetContributor(contributor))
    .keyBy(buildContributorKey)
    .map((rebalancedPosition, positionId) => {
      const security = get(originalPositions, positionId) ?? {}

      let percDelta = rebalancedPosition.positionPercentEquity - (get(security, "positionPercentEquity") ?? 0)
      percDelta /= 100
      const negative = percDelta < 0

      const position = rebalancedPosition ?? security
      // Not using get() default values here because it only defaults when the key doesn't exist or the value is undefined
      return {
        key: positionId,
        ...pick(position, ["ticker", "mic", "bloombergTicker", "figi", "country", "assetClass", "currency"]),
        classification: get(position, "classification.name"),
        securityPercentEquity: get(security, "positionPercentEquity") / 100 || 0,
        total: get(security, "risk.summary.total") || 0,
        specific: get(security, "risk.summary.specific") || 0,
        factorsTotal: get(security, "risk.summary.factors") || 0,
        rebalancedTotal: get(rebalancedPosition, "risk.summary.total") || 0,
        rebalancedSpecific: get(rebalancedPosition, "risk.summary.specific") || 0,
        rebalancedFactorsTotal: get(rebalancedPosition, "risk.summary.factors") || 0,
        tradePercentDelta: limitPrecision(percDelta),
        label: formatLabel(position),
        negative,
        traded: get(tradedSecuritiesMap, buildContributorKey(position)) || false,
      }
    })
    .value(),
)

export const getTradedPositions = createSelector(
  [getAllTrades],
  filterAndOrderTrades,
)

export const getTrades = createSelector(
  [getTradedPositions, getAllTrades],
  computeTradeBuckets,
)

export const getNumberOfTrades = createSelector(
  [getTrades],
  computeNumberOfTrades,
)

export const getExperiment = createSelector(
  [getRebalancedAssetHoldings, getRawPositionHoldings, getEquityOverride, getRebalanceDate, getSimulateActions, getActionsBase],
  (rebalancedPositionHoldings, originalPositionHoldings, equityOverride, rebalanceDate, simulateActions, actionsBase) => {
    const positions = isEmpty(rebalancedPositionHoldings) ? originalPositionHoldings : rebalancedPositionHoldings
    return computeSimulationExperiment(positions, actionsBase, simulateActions, equityOverride, rebalanceDate)
  },
)

function recursiveContributorMerge(obj, total, path) {
  /* Contributor schema
{
  ID: {
    positionPercentEquity,
    activePercentEquity,
    risk: {
      summary: { total, factors, specific },
      factors: {
        factorID: { id, name, category, value }
      }
    }
    exposure: {
      factors
    }
    ...moreProps
  }
}
*/
  // We iterate through the props, sum up number props otherwise, if its an object, recurse
  // We use updateWith(obj, path, updater, Object) to ensure non existing paths are created as part an object
  // This prevents an issue where having the first contributor with a modelProviderId of '111111118' would create
  // { contributors: Array(111111118) } instead of { contributors: { 111111118: { ... } } }
  forOwn(obj, (value, key) => {
    const propPath = path ? `${path}.${key}` : key
    // You can't add zScores
    if (key === "assetZScore") {
      updateWith(total, propPath, () => null, Object)
    } else if (isNumber(value)) {
      updateWith(total, propPath, (existing) => (existing ?? 0) + value, Object)
      // If its a factor property, we keep it (they are used later by column value getters)
    } else if (/factors\.(.*?)\./.test(propPath)) {
      updateWith(total, propPath, (existing) => existing ?? value, Object)
    } else if (isObject(value)) {
      recursiveContributorMerge(value, total, propPath)
    }
  })
}

function computeNotHeldContributor(contributors) {
  if (isEmpty(contributors)) {
    return undefined
  }

  return reduce(contributors, (total, position) => {
    if (!position.isNotHeld) {
      return total
    }
    recursiveContributorMerge(position, total)
    return total
  }, { ...NOTHELD_CONTRIBUTOR, isNotHeld: true, isAggregate: true, pinned: false })
}

function mapContributorsIsNotHeld(rebalancedContributors, contributors) {
  return mapValues(rebalancedContributors, (contributor, id) => ({
    ...contributor,
    // If a position was held in the original, it cannot be notHeld in the rebalance since it will be displayed
    // as a zeroed out position
    isNotHeld: contributor.isNotHeld && get(contributors, [id, "isNotHeld"]),
  }))
}

function containsContributors(notHeldContributor) {
  if (isEmpty(notHeldContributor)) { return false }

  return !chain(notHeldContributor)
    .keys()
    // We can't look at the metrics because UNMODELED securites won't have them so instead we check if any of the
    // percentEquityies, which come from composition, are set
    .intersection(["activePercentEquity", "basePercentEquity", "positionPercentEquity"])
    .isEmpty()
    .value()
}

function appendNotHeld(contributors, notHeldContributor, showNotHeld, isActive) {
  // Add a NOTHELD contributor if we are active and NOT showing notHeld positions
  const hasNotHeldContributors = isActive && some(contributors, "isNotHeld")
  if (notHeldContributor && hasNotHeldContributors && !showNotHeld) {
    return {
      ...contributors,
      NOTHELD: notHeldContributor,
    }
  }
  return contributors
}

// NOT GROUPED ------------------------------------------------
const getNotHeldContributor = createSelector(
  [getContributorsWithOriginalProperty],
  computeNotHeldContributor,
)

const getRebalancedContributorsWithNotHeldProperty = createSelector(
  [getRebalancedContributors, getContributorsWithOriginalProperty],
  mapContributorsIsNotHeld,
)

const getRebalancedNotHeldContributor = createSelector(
  [getRebalancedContributorsWithNotHeldProperty],
  (rebalancedContributors) => {
    const notHeldContributor = computeNotHeldContributor(rebalancedContributors)
    if (!containsContributors(notHeldContributor)) {
      return EMPTY_OBJECT
    }
    return notHeldContributor
  },
)

const getContributorsWithNotHeldContributor = createSelector(
  [getContributorsWithOriginalProperty, getNotHeldContributor, getIncludeNotHeld, getIsActiveAndNotIntraday],
  appendNotHeld,
)

const getRebalancedContributorsWithNotHeldContributor = createSelector(
  [getRebalancedContributors, getRebalancedNotHeldContributor, getIncludeNotHeld, getIsActiveAndNotIntraday],
  appendNotHeld,
)

const getCombinedContributorsWithNotHeldContributor = createSelector(
  [getContributorsWithNotHeldContributor, getRebalancedContributorsWithNotHeldContributor],
  combineContributors,
)

// GROUPED ------------------------------------------------
const getGroupedNotHeldContributor = createSelector(
  [getGroupedContributors],
  computeNotHeldContributor,
)

const getRebalancedGroupedContributorsWithNotHeld = createSelector(
  [getGroupedRebalancedContributors, getGroupedContributors],
  mapContributorsIsNotHeld,
)

const getRebalancedGroupedNotHeldContributor = createSelector(
  [getRebalancedGroupedContributorsWithNotHeld],
  computeNotHeldContributor,
)

const getGroupedContributorsWithNotHeldContributor = createSelector(
  [getGroupedContributors, getGroupedNotHeldContributor, getIncludeNotHeld, getIsActiveAndNotIntraday],
  appendNotHeld,
)

const getRebalancedGroupedContributorsWithNotHeldContributor = createSelector(
  [getRebalancedGroupedContributorsWithNotHeld, getRebalancedGroupedNotHeldContributor, getIncludeNotHeld, getIsActiveAndNotIntraday],
  appendNotHeld,
)

const getCombinedGroupedContributorsWithNotHeldContributor = createSelector(
  [getGroupedContributorsWithNotHeldContributor, getRebalancedGroupedContributorsWithNotHeldContributor],
  combineContributors,
)

// Choose contributors between NON GROUPED or GROUPED
const getContributorsData = createSelector(
  [getCombinedContributorsWithNotHeldContributor, getCombinedGroupedContributorsWithNotHeldContributor, getGrouping, getSelectedGroup],
  pickContributorsOrGroupedContributors,
)

export const getSortedContributorsData = createSelector(
  [getContributorsData, getOrder, getAllColumns, getIncludeNotHeld, getIsActive, getSimulateActions],
  (contributors, order, columns, showNotHeld, isActive, simulateActions) => {
    const { id: orderBy, direction, absolute } = order
    const orderByIteratee = (contributor) => {
      if (orderBy === "id") {
        return get(contributor, "id")
      }
      const column = find(columns, { id: orderBy })
      const getter = get(column, "getter", identity)
      const value = getter(contributor, get(column, "path"), orderBy) ?? NaN
      return absolute ? Math.abs(value) : value
    }

    // We orderBy held securities first so we can see them even while showing all of a benchmarks positions
    return chain(contributors)
      .thru(contributors => {
        if (showNotHeld && isActive) {
          return map(contributors)
        }
        return filter(contributors, ({ id, isNotHeld }) => !isNotHeld || id === "NOTHELD")
      })
      .map(contributor => ({
        ...contributor,
        pinned: some(simulateActions, (simulateAction) => isMatchingContributorOrSubContributor(get(simulateAction, "tradeSecurity.id"), contributor)),
      }))
      .partition(position => isNaN(orderByIteratee(position)) || orderByIteratee(position) == null)
      .thru(([nils, values]) => concat(
        _orderBy(values, ...[
          compact([
            ({ pinned }) => pinned,
            showNotHeld ? ({ isNotHeld }) => isNotHeld : null,
            orderByIteratee,
          ]),
          compact([
            "desc",
            showNotHeld ? "asc" : null,
            direction,
          ]),
        ]), _orderBy(nils, [(contributor) => get(contributor, "id")], [direction ?? "asc"])))
      .value()
  },
)

export const getNumberOfContributors = createSelector(
  [getSortedContributorsData],
  (contributors) => size(contributors),
)

export const getSortedAndPaginatedData = createSelector(
  [getSortedContributorsData, getSelectedPage],
  (sortedData, selectedPage) => {
    const start = selectedPage * ITEMS_PER_PAGE
    return sortedData.slice(start, start + ITEMS_PER_PAGE)
  },
)

export const getSortedAndPaginatedDataWithEditable = createSelector(
  [getSortedAndPaginatedData, getEditingSecurity, getContributors, getRebalancedContributors, getGroupedContributors, getGroupedRebalancedContributors, getSelectedGroup, getGrouping],
  (sortedData, editingSecurity, contributors, rebalancedContributors, groupedContributors, groupedRebalancedContributors, selectedGroup, grouping) => {
    const sortedDataWithoutCashOffset = filter(sortedData, security => !isCashOffsetContributor(security))
    const editingIndex = findIndex(sortedDataWithoutCashOffset, (contributor) => contributor.id === editingSecurity?.id)
    if (editingIndex === -1) {
      return sortedDataWithoutCashOffset
    }

    const isGroupView = !isEmpty(grouping) && !selectedGroup
    const contributor = get(sortedDataWithoutCashOffset, editingIndex)
    const contributorId = buildContributorKey(contributor)
    let original = isGroupView ? get(groupedContributors, contributorId) : get(contributors, contributorId)
    let rebalanced = isGroupView ? get(groupedRebalancedContributors, contributorId) : get(rebalancedContributors, contributorId)
    if (selectedGroup) {
      original = get(groupedContributors, [selectedGroup, "contributors", contributorId])
      rebalanced = get(groupedRebalancedContributors, [selectedGroup, "contributors", contributorId])
    }
    const delta = chain(rebalanced || original)
      .cloneDeep()
      .mergeWith(original, (objValue, srcValue, key) => {
        if (includes(["economicExposure", "positionPercentEquity", "activePercentEquity"], key)) {
          return (objValue ?? 0) - (srcValue ?? 0)
        }
        return (!isFinite(objValue) || !isFinite(srcValue)) ? undefined : objValue - srcValue
      })
      .value()

    const items = [
      { ...(original || rebalanced), isEditable: false, label: `${isGroupView ? contributor.label : contributor.ticker} - O`, classnames: { "is-editing": true, original: true } },
      { ...(rebalanced || original), isEditable: !isGroupView, label: "Rebalanced", id: "rebalanced", originalId: contributor.id, classnames: { "is-editing": true } },
      { ...delta, original, isEditable: !isGroupView, label: "Delta", id: "delta", originalId: contributor.id, classnames: { "is-editing": true } },
    ]

    return [...slice(sortedDataWithoutCashOffset, 0, editingIndex), ...items, ...slice(sortedDataWithoutCashOffset, editingIndex + 1)]
  },
)

// If a contributor exists in the original but not the rebalanced, it means the position was zeroed out
// isNotHeld needs to take into account the original positions
function computeIsNotHeld(original, rebalanced) {
  if (!rebalanced) {
    return original
  }
  return rebalanced && original
}

function combineContributors(contributors, rebalancedContributors) {
  if (isEmpty(rebalancedContributors)) {
    return contributors
  }

  return chain({})
    .mergeWith(omit(contributors, "NOTHELD"), rebalancedContributors, (original, rebalanced, key) => key === "isNotHeld" ? computeIsNotHeld(original, rebalanced) : rebalanced)
    .mapValues((contributor, id) => ({
      ...contributor,
      original: get(contributors, [id, "original"]),
    }))
    .value()
}

const getAggregatedContributors = createSelector(
  [getCombinedContributorsWithNotHeldContributor],
  (contributors) => {
    // Ignore the rebalanced, delta and NOTHELD contributors as they are not part of the original positions
    const baseSecurities = filter(contributors, ({ id }) => !includes(["rebalanced", "delta", "NOTHELD"], id))

    return reduce(baseSecurities, (total, position) => {
      recursiveContributorMerge(position, total)
      return total
    }, { id: "TOTAL", isAggregate: true })
  },
)

// This is the computed Total row in the table
const getComputedAggregatedData = createSelector(
  [getAggregatedContributors, getSummary, getRebalancedSummary],
  (aggregatedContributors, originalSummary, rebalancedSummary) => {
    const summary = rebalancedSummary || originalSummary
    const riskAttribution = get(summary, "risk.summary.total")
    const newAggregatedContributors = cloneDeep(aggregatedContributors)

    // Total risk also needs to display riskAttribution as a tooltip
    set(newAggregatedContributors, "risk.summary.total", {
      value: get(newAggregatedContributors, "risk.summary.total"),
      tooltipContents: [`Total Risk: ${percentageFormatter(riskAttribution)}%`],
    })

    return {
      ...newAggregatedContributors,
      action: Creators.setSelectedGroup,
    }
  },
)

// AggregatedData represents the total row in the table. When there is no selected group, it is calculated (computedAggregatedData)
// but when there is a selectedGroup, it means we are looking at the contributors of that group so the total row is actually the
// group contributor item itself
export const getAggregatedData = createSelector(
  [getComputedAggregatedData, getCombinedGroupedContributorsWithNotHeldContributor, getSelectedGroup],
  (computedAggregatedData, contributors, selectedGroup) => {
    const isDrillDown = !!selectedGroup
    if (isDrillDown) {
      return {
        ...get(contributors, selectedGroup),
        isDrillDown: true,
        isAggregate: true,
        action: Creators.setSelectedGroup,
      }
    }
    return computedAggregatedData
  },
)

export const getGroupDropdown = createSelector(
  [getGroupedPortfolioPositions, getGroupedRebalancedPortfolioPositions, getGrouping, getColumnsByCategory, getSelectedGroup],
  (groupedPositions, rebalancedGroupedPositions, currentGroup, columns, selectedGroup) => {
    const data = !isEmpty(rebalancedGroupedPositions) ? rebalancedGroupedPositions : groupedPositions
    if (some([data, currentGroup, columns, selectedGroup], isEmpty)) {
      return null
    }

    // Get descriptor columns (they are inside the Descriptors group)
    const descriptors = chain(columns)
      .get([0, "columns"])
      .filter(({ category }) => isEmpty(category))
      .map(column => ({ ...column, getter: (item) => get(item, column.path) }))
      .value()

    return ({
      columns: [
        { id: "label", label: get(currentGroup, "label"), align: "left", getter: get },
        ...descriptors,
      ],
      items: map(data, (group) => ({
        ...group,
        label: group.description,
      })),
      selectedId: selectedGroup,
      backAction: Creators.hideGroup(),
    })
  },
)

function pickContributorsOrGroupedContributors(contributors, groupedContributors, grouping, selectedGroup) {
  if (grouping && selectedGroup) {
    return get(groupedContributors, [selectedGroup, "contributors"])
  }

  if (grouping) {
    return groupedContributors
  }

  return contributors
}
